import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Vault, Plus, X, Eye, EyeSlash, Copy, Pencil, Trash, ArrowsClockwise, MagnifyingGlass, Lock, UsersThree, ShieldCheck, ClockCounterClockwise, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtIstDateTime } from '../lib/datetime';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICON = {
  Bank: '🏦', Gmail: '📧', Envato: '🎨', Canva: '🖼️', ChatGPT: '🤖',
  'Putty / SSH': '💻', FTP: '📁', Hosting: '☁️', Domain: '🌐',
  WordPress: '📝', 'Social Media': '📱', 'Payment Gateway': '💳', Other: '🔑',
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
      return true;
    }
    // Legacy fallback for insecure contexts / older browsers
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) toast.success('Copied to clipboard');
    else toast.error('Copy blocked — your browser denied clipboard access');
    return ok;
  } catch (err) {
    // navigator.clipboard.writeText rejects with NotAllowedError in headless / insecure context.
    // Don't let the rejection bubble to React's error boundary — surface a useful toast instead.
    toast.error('Copy blocked — your browser denied clipboard access');
    return false;
  }
};

const initial = (n) => (n || '?').charAt(0).toUpperCase();

const passwordStrength = (p = '') => {
  if (!p) return { label: '—', color: '#94a3b8', score: 0 };
  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  if (score <= 1) return { label: 'Weak', color: '#dc2626', score };
  if (score <= 3) return { label: 'OK', color: '#d97706', score };
  return { label: 'Strong', color: '#15803d', score };
};

const generatePassword = (len = 18) => {
  const charset = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*()_-+=';
  const out = [];
  const buf = new Uint32Array(len);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  for (let i = 0; i < len; i++) out.push(charset[buf[i] % charset.length]);
  return out.join('');
};

export default function VaultPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'Other', username: '', url: '', notes: '', password: '', shared_with_user_ids: [] });
  const [showPwInForm, setShowPwInForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  // Per-row reveal state: { [entryId]: 'decrypted-password' }
  const [revealed, setRevealed] = useState({});
  const autoHideRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat !== 'all') params.category = filterCat;
      if (query) params.q = query;
      // Load each endpoint independently — one failure shouldn't blank the others.
      const [r, c, u] = await Promise.all([
        axios.get(`${API_URL}/api/vault`, { params, withCredentials: true }).catch((e) => {
          const msg = e.response?.data?.detail || e.message;
          if (e.response?.status === 503) toast.error(msg, { duration: 12000 });
          else if (e.response?.status === 403) toast.error(msg, { duration: 8000 });
          else toast.error('Failed to load vault entries: ' + msg);
          return { data: [] };
        }),
        axios.get(`${API_URL}/api/vault/categories`, { withCredentials: true })
          .catch((e) => {
            console.warn('Vault categories failed:', e.response?.data?.detail || e.message);
            // Fallback to client-side preset list so the dropdown is never empty
            return { data: ['Bank', 'Gmail', 'Envato', 'Canva', 'ChatGPT', 'Putty / SSH', 'FTP', 'Hosting', 'Domain', 'WordPress', 'Social Media', 'Payment Gateway', 'Other'] };
          }),
        axios.get(`${API_URL}/api/vault/share-users`, { withCredentials: true })
          .catch(() => ({ data: [] })),
      ]);
      setList(r.data || []);
      setCategories(c.data || []);
      setUsers(u.data || []);
    } catch (e) {
      // Should be unreachable now that each call has its own catch — leave for safety
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [filterCat, query]);

  useEffect(() => { load(); }, [load]);

  const myId = user?.id || user?._id || '';

  const counts = useMemo(() => {
    const map = {};
    for (const e of list) map[e.category] = (map[e.category] || 0) + 1;
    return map;
  }, [list]);

  const startNew = () => {
    setEditing({ id: null });
    setForm({ title: '', category: 'Other', username: '', url: '', notes: '', password: '', shared_with_user_ids: [] });
    setShowPwInForm(false);
    setOpen(true);
  };
  const startEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || '', category: e.category || 'Other',
      username: e.username || '', url: e.url || '', notes: e.notes || '',
      password: '',  // never prefilled — left empty means "don't change"
      shared_with_user_ids: e.shared_with_user_ids || [],
    });
    setShowPwInForm(false);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!editing?.id && !form.password) return toast.error('Password is required');
    setSaving(true);
    try {
      if (editing?.id) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await axios.put(`${API_URL}/api/vault/${editing.id}`, payload, { withCredentials: true });
        toast.success('Vault entry updated');
      } else {
        await axios.post(`${API_URL}/api/vault`, form, { withCredentials: true });
        toast.success('Saved to vault');
      }
      setOpen(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? The password cannot be recovered after this.`)) return;
    try {
      await axios.delete(`${API_URL}/api/vault/${e.id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const reveal = async (e) => {
    if (revealed[e.id]) {
      // Toggle off
      setRevealed((s) => { const n = { ...s }; delete n[e.id]; return n; });
      if (autoHideRef.current[e.id]) { clearTimeout(autoHideRef.current[e.id]); delete autoHideRef.current[e.id]; }
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/vault/${e.id}/reveal`, {}, { withCredentials: true });
      if (r.data?._decrypt_error) toast.error(r.data._decrypt_error);
      setRevealed((s) => ({ ...s, [e.id]: r.data.password || '' }));
      // Auto-hide after 30s for safety
      autoHideRef.current[e.id] = setTimeout(() => {
        setRevealed((s) => { const n = { ...s }; delete n[e.id]; return n; });
      }, 30000);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const copyPassword = async (e) => {
    try {
      // Prefer already-revealed value, else fetch transparently
      let pw = revealed[e.id];
      if (!pw) {
        const r = await axios.post(`${API_URL}/api/vault/${e.id}/reveal`, {}, { withCredentials: true });
        pw = r.data?.password || '';
      }
      if (!pw) return toast.error('Could not retrieve password');
      await copyToClipboard(pw);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  // Esc-to-close for both modals (matches shadcn dialog conventions)
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        if (open) setOpen(false);
        if (auditOpen) setAuditOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, auditOpen]);

  const openAudit = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/vault/audit?limit=200`, { withCredentials: true });
      setAudit(r.data || []);
      setAuditOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const filterShareUserList = users.filter((u) => u.id !== myId);

  return (
    <div className="space-y-4" data-testid="vault-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <Vault size={22} weight="duotone" /> Password Vault
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
            <ShieldCheck size={12} weight="fill" className="text-emerald-600" />
            AES-256 encrypted at rest. Passwords are decrypted only when you click <em className="px-1">Reveal</em>. Every reveal is audit-logged.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={openAudit} data-testid="vault-audit-btn">
              <ClockCounterClockwise size={14} className="mr-1" /> Audit Log
            </Button>
          )}
          <Button onClick={startNew} data-testid="vault-new-btn">
            <Plus size={14} className="mr-1" /> New Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px]">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="title, username, url, notes…"
              className="pl-8"
              data-testid="vault-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px]">Category</Label>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="border rounded px-2 py-1.5 text-xs bg-white"
            data-testid="vault-cat-filter"
          >
            <option value="all">All ({list.length})</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_ICON[c] || '🔑'} {c} {counts[c] ? `(${counts[c]})` : ''}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="vault-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> Refresh
        </Button>
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-gray-500">Loading…</Card>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center text-sm text-gray-500" data-testid="vault-empty">
          <Lock size={28} className="mx-auto mb-2 text-gray-300" />
          {query || filterCat !== 'all' ? 'No entries match your filters.' : 'Your vault is empty. Click "New Entry" to save your first credential.'}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vault-list">
          {list.map((e) => {
            const isRevealed = !!revealed[e.id];
            return (
              <Card key={e.id} className="p-3 border-l-4" style={{ borderLeftColor: e.is_owner ? '#1D9E75' : '#3b82f6' }} data-testid={`vault-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-2xl flex-shrink-0">{CATEGORY_ICON[e.category] || '🔑'}</div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate" style={{ color: '#1a2e4a' }} data-testid={`vault-title-${e.id}`}>{e.title}</div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{e.category}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {e.can_edit && (
                      <button onClick={() => startEdit(e)} className="p-1 text-gray-400 hover:text-gray-700" data-testid={`vault-edit-${e.id}`}>
                        <Pencil size={14} />
                      </button>
                    )}
                    {e.can_edit && (
                      <button onClick={() => remove(e)} className="p-1 text-gray-400 hover:text-rose-600" data-testid={`vault-del-${e.id}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {e.username && (
                  <div className="mt-2 flex items-center gap-1 text-xs">
                    <span className="text-gray-500 w-16">Username</span>
                    <span className="font-mono flex-1 truncate text-gray-800" data-testid={`vault-username-${e.id}`}>{e.username}</span>
                    <button onClick={() => copyToClipboard(e.username)} className="p-1 text-gray-400 hover:text-emerald-600" title="Copy username"><Copy size={12} /></button>
                  </div>
                )}

                <div className="mt-1 flex items-center gap-1 text-xs">
                  <span className="text-gray-500 w-16">Password</span>
                  <span className="font-mono flex-1 truncate text-gray-800" data-testid={`vault-password-${e.id}`}>
                    {isRevealed ? revealed[e.id] : '••••••••••••'}
                  </span>
                  <button
                    onClick={() => reveal(e)}
                    className={`p-1 ${isRevealed ? 'text-emerald-600' : 'text-gray-400 hover:text-emerald-600'}`}
                    title={isRevealed ? 'Hide' : 'Reveal'}
                    data-testid={`vault-reveal-${e.id}`}
                  >
                    {isRevealed ? <EyeSlash size={12} /> : <Eye size={12} />}
                  </button>
                  <button onClick={() => copyPassword(e)} className="p-1 text-gray-400 hover:text-emerald-600" title="Copy password" data-testid={`vault-copy-${e.id}`}><Copy size={12} /></button>
                </div>

                {e.url && (
                  <div className="mt-1 flex items-center gap-1 text-xs">
                    <span className="text-gray-500 w-16">URL</span>
                    <a href={e.url} target="_blank" rel="noreferrer" className="text-blue-600 underline truncate flex-1 inline-flex items-center gap-1">
                      <Globe size={11} /> {e.url}
                    </a>
                  </div>
                )}

                {e.notes && (
                  <div className="mt-2 text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1 whitespace-pre-wrap">{e.notes}</div>
                )}

                <div className="mt-2 pt-2 border-t flex items-center justify-between text-[10px] text-gray-400">
                  <span>Owner: {e.is_owner ? 'you' : e.created_by_name || '—'}</span>
                  {(e.shared_with_names || []).length > 0 && (
                    <span className="inline-flex items-center gap-1" title={`Shared with: ${e.shared_with_names.join(', ')}`}>
                      <UsersThree size={11} /> {e.shared_with_names.length} shared
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal: New / Edit */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <Card className="w-full max-w-lg bg-white p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vault-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
                <Lock size={16} /> {editing?.id ? 'Edit Vault Entry' : 'New Vault Entry'}
              </h3>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. ICICI Bank — primary account" data-testid="vault-title-input" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Category</Label>
                  <select
                    className="w-full border rounded px-2 py-2 text-sm bg-white"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="vault-category-input"
                  >
                    {categories.map((c) => <option key={c} value={c}>{CATEGORY_ICON[c] || '🔑'} {c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">URL</Label>
                  <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="vault-url-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Username / Identifier</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="email / account number / username" data-testid="vault-username-input" />
              </div>
              <div>
                <Label className="text-xs">
                  Password {editing?.id ? <span className="text-gray-500 font-normal">(leave blank to keep current)</span> : '*'}
                </Label>
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <Input
                      type={showPwInForm ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={editing?.id ? 'Leave blank to keep current' : 'Enter password'}
                      className="pr-9 font-mono"
                      data-testid="vault-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwInForm((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      data-testid="vault-password-toggle"
                    >
                      {showPwInForm ? <EyeSlash size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setForm({ ...form, password: generatePassword(18) }); setShowPwInForm(true); }}
                    data-testid="vault-password-generate"
                    title="Generate a strong random password"
                  >
                    Generate
                  </Button>
                </div>
                {form.password && (() => {
                  const s = passwordStrength(form.password);
                  return (
                    <div className="flex items-center gap-2 mt-1 text-[10px]">
                      <div className="flex-1 h-1 bg-slate-200 rounded overflow-hidden">
                        <div style={{ width: `${(s.score / 5) * 100}%`, background: s.color, height: '100%' }} />
                      </div>
                      <span style={{ color: s.color }} className="font-bold uppercase">{s.label}</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea
                  className="w-full border rounded px-2 py-2 text-sm min-h-[60px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Recovery email, security questions, 2FA notes…"
                  data-testid="vault-notes-input"
                />
              </div>
              <div>
                <Label className="text-xs">Share with ({form.shared_with_user_ids.length} selected)</Label>
                <div className="text-[10px] text-gray-500 mb-1">
                  Only users who already have Vault access can be shared an entry. Admins always have access. To grant a staff member Vault access, go to Settings → Users.
                </div>
                <div className="max-h-[160px] overflow-y-auto border rounded p-1.5 space-y-0.5">
                  {filterShareUserList.length === 0 && <div className="text-xs text-gray-400 p-2">No other vault-enabled users to share with.</div>}
                  {filterShareUserList.map((u) => {
                    const checked = form.shared_with_user_ids.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 text-xs py-1 px-1 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? form.shared_with_user_ids.filter((x) => x !== u.id) : [...form.shared_with_user_ids, u.id];
                            setForm({ ...form, shared_with_user_ids: next });
                          }}
                          data-testid={`vault-share-${u.id}`}
                        />
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: '#1D9E75' }}>{initial(u.name)}</div>
                        <span className="font-semibold">{u.name}</span>
                        <span className="text-[10px] text-gray-400 ml-auto">{u.email}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="vault-save-btn">
                {saving ? 'Saving…' : (editing?.id ? 'Update' : 'Save to Vault')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Audit modal (admin only) */}
      {auditOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAuditOpen(false)}>
          <Card className="w-full max-w-2xl bg-white p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vault-audit-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
                <ClockCounterClockwise size={16} /> Vault Audit Log
              </h3>
              <button onClick={() => setAuditOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            {audit.length === 0 ? (
              <div className="text-sm text-gray-500 text-center p-6">No reveal events yet.</div>
            ) : (
              <div className="space-y-1">
                {audit.map((a, i) => (
                  <div key={i} className="text-xs flex items-center gap-2 py-1.5 border-b last:border-0">
                    <Eye size={11} className="text-gray-400" />
                    <span className="font-semibold">{a.user_name}</span>
                    <span className="text-gray-500">revealed</span>
                    <span className="font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{a.entry_title}</span>
                    <span className="ml-auto text-gray-400">{fmtIstDateTime(a.at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
